import React, { useEffect, useMemo, useState } from "react";
import {
  loadTemplates,
  saveTemplates,
  loadSites,
  saveSites,
  newId,
} from "./siteCheckStore";
import { Template, Site, OrgUser } from "./types";
import SiteCheckTemplateEditor from "./SiteCheckTemplateEditor";
import SiteCheckAssignCrewModal from "./SiteCheckAssignCrewModal";
import "./sitecheck.css";

type Tab = "sites" | "templates";

interface ApiUser {
  user_id?: string;
  id?: string;
  email?: string;
  name?: string;
  customRoles?: string[];
}

interface ApiRole {
  id: string;
  permissions?: string[];
}

interface RolesResponse {
  org?: ApiRole[];
}

export default function ManageSiteCheck() {
  const [tab, setTab] = useState<Tab>("sites");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [assigningSiteId, setAssigningSiteId] = useState<string | null>(null);
  const [users, setUsers] = useState<OrgUser[]>([]);

  // Initial load — templates / sites come from /sitecheck/* endpoints.
  // Both calls fire in parallel; failure logs only so the page renders
  // with empty lists rather than crashing.
  useEffect(() => {
    let cancelled = false;
    Promise.all([loadTemplates(), loadSites()])
      .then(([t, s]) => {
        if (cancelled) return;
        setTemplates(t);
        setSites(s);
      })
      .catch((err) => console.error("[ManageSiteCheck] initial load failed:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull org users for crew/manager pickers. Reuses the same pattern
  // ManageSMRTCOP uses (approved_users + role-permission filter), but
  // accepts anyone with siteCheck.view OR siteCheck.admin so a brand-
  // new org doesn't show an empty list before roles are configured.
  // Falls back to ALL approved users if the role list is empty.
  useEffect(() => {
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, {
        method: "GET",
        credentials: "include",
      }).then((r) => r.json() as Promise<ApiUser[] | unknown>),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/roles?scope=all`, {
        method: "GET",
        credentials: "include",
      }).then((r) => r.json() as Promise<RolesResponse | unknown>),
    ])
      .then(([data, rolesData]) => {
        const orgRoles: ApiRole[] = (rolesData as RolesResponse)?.org || [];
        const allowedRoleIds = new Set(
          orgRoles
            .filter(
              (r) =>
                r.permissions?.includes("siteCheck.view") ||
                r.permissions?.includes("siteCheck.admin"),
            )
            .map((r) => r.id),
        );
        const arr: ApiUser[] = Array.isArray(data) ? (data as ApiUser[]) : [];
        const filtered = allowedRoleIds.size
          ? arr.filter((u) =>
              (u.customRoles || []).some((rid) => allowedRoleIds.has(rid)),
            )
          : arr;
        setUsers(
          filtered.map((u) => ({
            id: u.user_id || u.id || u.email || "",
            name: u.name || u.email || "(unnamed)",
          })),
        );
      })
      .catch((err) => console.error("[SiteCheck] failed to load users", err));
  }, []);

  // ── Persistence wrappers ─────────────────────────────────────────────────
  // setState first for snappy UI, then PUT to backend. Saves are async
  // and fire-and-forget; on failure we log but keep the local state so
  // the user's next edit will retry the full list.

  const persistTemplates = (next: Template[]) => {
    setTemplates(next);
    saveTemplates(next).catch((err) =>
      console.error("[ManageSiteCheck] save templates failed:", err),
    );
  };

  const persistSites = (next: Site[]) => {
    setSites(next);
    saveSites(next).catch((err) =>
      console.error("[ManageSiteCheck] save sites failed:", err),
    );
  };

  // ── Template ops ─────────────────────────────────────────────────────────

  const createTemplate = () => {
    const t: Template = {
      id: newId("tpl"),
      name: "New Template",
      sections: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    persistTemplates([...templates, t]);
    setEditingTemplateId(t.id);
  };

  const updateTemplate = (next: Template) => {
    persistTemplates(templates.map((t) => (t.id === next.id ? next : t)));
  };

  const deleteTemplate = (id: string) => {
    if (!window.confirm("Delete this template? Sites using it will be orphaned.")) return;
    persistTemplates(templates.filter((t) => t.id !== id));
  };

  // ── Site ops ─────────────────────────────────────────────────────────────

  const createSite = () => {
    const s: Site = {
      id: newId("site"),
      name: "New Site",
      address: "",
      templateId: templates[0]?.id || "",
      assignedUserIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    persistSites([...sites, s]);
    setEditingSiteId(s.id);
  };

  const updateSite = (id: string, patch: Partial<Site>) => {
    persistSites(
      sites.map((s) =>
        s.id === id ? { ...s, ...patch, updatedAt: new Date().toISOString() } : s,
      ),
    );
  };

  const deleteSite = (id: string) => {
    if (!window.confirm("Delete this site? Photos and answers stay until you clear browser data.")) return;
    persistSites(sites.filter((s) => s.id !== id));
  };

  const editingTemplate = useMemo(
    () => templates.find((t) => t.id === editingTemplateId) || null,
    [templates, editingTemplateId],
  );

  const editingSite = useMemo(
    () => sites.find((s) => s.id === editingSiteId) || null,
    [sites, editingSiteId],
  );

  const assigningSite = useMemo(
    () => sites.find((s) => s.id === assigningSiteId) || null,
    [sites, assigningSiteId],
  );

  return (
    <div className="sc-page">
      <div className="sc-page-header">
        <h1>Manage Site Check</h1>
        <p className="sc-subtle">Local demo — data stored in this browser only.</p>
      </div>

      <div className="sc-tabs">
        <button
          className={`sc-tab ${tab === "sites" ? "active" : ""}`}
          onClick={() => setTab("sites")}
        >Sites</button>
        <button
          className={`sc-tab ${tab === "templates" ? "active" : ""}`}
          onClick={() => setTab("templates")}
        >Templates</button>
      </div>

      {tab === "templates" && (
        <div className="sc-tab-body">
          <div className="sc-toolbar">
            <button className="sc-btn-primary" onClick={createTemplate}>+ New Template</button>
          </div>
          {templates.length === 0 && (
            <p className="sc-empty">No templates yet. Create one to get started.</p>
          )}
          <ul className="sc-list">
            {templates.map((t) => (
              <li key={t.id} className="sc-list-row">
                <div className="sc-list-main">
                  <strong>{t.name}</strong>
                  <span className="sc-subtle">
                    {t.sections.length} section{t.sections.length === 1 ? "" : "s"}
                    {" · "}
                    {t.sections.reduce((n, s) => n + s.questions.length, 0)} question
                    {t.sections.reduce((n, s) => n + s.questions.length, 0) === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="sc-list-actions">
                  <button className="sc-btn-secondary" onClick={() => setEditingTemplateId(t.id)}>Edit</button>
                  <button className="sc-btn-danger" onClick={() => deleteTemplate(t.id)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "sites" && (
        <div className="sc-tab-body">
          <div className="sc-toolbar">
            <button
              className="sc-btn-primary"
              onClick={createSite}
              disabled={templates.length === 0}
              title={templates.length === 0 ? "Create a template first" : ""}
            >+ New Site</button>
          </div>
          {sites.length === 0 && (
            <p className="sc-empty">No sites yet. Create a template, then add a site.</p>
          )}
          <ul className="sc-list">
            {sites.map((s) => {
              const tpl = templates.find((t) => t.id === s.templateId);
              return (
                <li key={s.id} className="sc-list-row sc-site-row">
                  <div className="sc-list-main">
                    <strong>{s.name}</strong>
                    <span className="sc-subtle">
                      {tpl?.name || "(no template)"} · {s.assignedUserIds.length} crew
                    </span>
                  </div>
                  <div className="sc-list-actions">
                    <button className="sc-btn-secondary" onClick={() => setEditingSiteId(s.id)}>Edit</button>
                    <button className="sc-btn-secondary" onClick={() => setAssigningSiteId(s.id)}>Assign</button>
                    <button className="sc-btn-danger" onClick={() => deleteSite(s.id)}>Delete</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {editingTemplate && (
        <SiteCheckTemplateEditor
          template={editingTemplate}
          onChange={updateTemplate}
          onClose={() => setEditingTemplateId(null)}
        />
      )}

      {editingSite && (
        <SiteEditModal
          site={editingSite}
          templates={templates}
          onChange={(patch) => updateSite(editingSite.id, patch)}
          onClose={() => setEditingSiteId(null)}
        />
      )}

      {assigningSite && (
        <SiteCheckAssignCrewModal
          isOpen={!!assigningSite}
          onClose={() => setAssigningSiteId(null)}
          users={users}
          initialCrewIds={assigningSite.assignedUserIds}
          onSubmit={(crewIds) => {
            updateSite(assigningSite.id, { assignedUserIds: crewIds });
            setAssigningSiteId(null);
          }}
        />
      )}
    </div>
  );
}

// ── Inline site-edit modal — fields only. Crew assignment lives in the
// dedicated assign modal so the picker's user list isn't loaded on
// every site edit.
function SiteEditModal({
  site,
  templates,
  onChange,
  onClose,
}: {
  site: Site;
  templates: Template[];
  onChange: (patch: Partial<Site>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(site.name);
  const [address, setAddress] = useState(site.address || "");
  const [lat, setLat] = useState(site.lat?.toString() ?? "");
  const [lng, setLng] = useState(site.lng?.toString() ?? "");
  const [templateId, setTemplateId] = useState(site.templateId);
  // Meta fields mirror SMRT COP's site metadata (Site ID, FA Location
  // Code, sector azimuths). Stored loose-typed as strings so the user
  // can paste headings like "70°" without parse errors.
  const [siteCode, setSiteCode] = useState(site.meta?.siteCode || "");
  const [faLocationCode, setFaLocationCode] = useState(site.meta?.faLocationCode || "");
  const [azA, setAzA] = useState(site.meta?.azimuthA || "");
  const [azB, setAzB] = useState(site.meta?.azimuthB || "");
  const [azC, setAzC] = useState(site.meta?.azimuthC || "");

  const save = () => {
    onChange({
      name: name.trim() || "Untitled site",
      address: address.trim(),
      lat: lat ? parseFloat(lat) : undefined,
      lng: lng ? parseFloat(lng) : undefined,
      templateId,
      meta: {
        siteCode: siteCode.trim() || undefined,
        faLocationCode: faLocationCode.trim() || undefined,
        azimuthA: azA.trim() || undefined,
        azimuthB: azB.trim() || undefined,
        azimuthC: azC.trim() || undefined,
      },
    });
    onClose();
  };

  return (
    <div className="sc-modal-overlay">
      <div className="sc-modal" style={{ width: 520 }}>
        <div className="sc-modal-header">
          <h2>Edit Site</h2>
          <button className="sc-btn-cancel" onClick={onClose}>Cancel</button>
        </div>
        <div className="sc-modal-body">
          <label className="sc-field">
            <span>Site Name</span>
            <input className="sc-input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <div className="sc-field-row">
            <label className="sc-field">
              <span>Site ID</span>
              <input
                className="sc-input"
                value={siteCode}
                onChange={(e) => setSiteCode(e.target.value)}
                placeholder="e.g. WTL01995_3"
              />
            </label>
            <label className="sc-field">
              <span>FA Location Code</span>
              <input
                className="sc-input"
                value={faLocationCode}
                onChange={(e) => setFaLocationCode(e.target.value)}
                placeholder="e.g. 10085227"
              />
            </label>
          </div>
          <label className="sc-field">
            <span>Physical Address</span>
            <input className="sc-input" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <div className="sc-field-row">
            <label className="sc-field">
              <span>Latitude</span>
              <input
                className="sc-input"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="e.g. 31.1664169"
              />
            </label>
            <label className="sc-field">
              <span>Longitude</span>
              <input
                className="sc-input"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                placeholder="e.g. -105.6573331"
              />
            </label>
          </div>
          <div className="sc-field">
            <span>Azimuths</span>
            <div className="sc-field-row">
              <label className="sc-field">
                <span style={{ fontSize: "0.7rem" }}>Sector A</span>
                <input
                  className="sc-input"
                  value={azA}
                  onChange={(e) => setAzA(e.target.value)}
                  placeholder="70"
                />
              </label>
              <label className="sc-field">
                <span style={{ fontSize: "0.7rem" }}>Sector B</span>
                <input
                  className="sc-input"
                  value={azB}
                  onChange={(e) => setAzB(e.target.value)}
                  placeholder="190"
                />
              </label>
              <label className="sc-field">
                <span style={{ fontSize: "0.7rem" }}>Sector C</span>
                <input
                  className="sc-input"
                  value={azC}
                  onChange={(e) => setAzC(e.target.value)}
                  placeholder="310"
                />
              </label>
            </div>
          </div>
          <label className="sc-field">
            <span>Template</span>
            <select
              className="sc-input"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.length === 0 && <option value="">— none —</option>}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="sc-modal-footer">
          <button className="sc-btn-primary" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}
